import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  Area,
  AreaChart,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { t } from '../../utils/i18n';
import { PlatformLegendLabel } from '../../components/PlatformBadge/PlatformBadge';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import {
  getSignupsTimeseries,
  getSignupsByMonth,
  getDashboardOverview,
  getDevicesRegistrationSummary,
  getDevicesDistribution,
} from '../../api/authAdmin';
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_PRIMARY,
  darkChartTooltipProps,
  formatChartDate,
} from '../../utils/gaDashboard';
import AnalyticsSummary from '../Analytics/AnalyticsSummary';
import OpsPulseSection from './ops/OpsPulseSection';
import DashboardPanel from './components/DashboardPanel';
import DashboardKpi from './components/DashboardKpi';
import ChartSkeleton from './components/ChartSkeleton';

const TIMESERIES_DAYS = 30;
const MONTHS_RANGE = 12;

const DEVICE_PIE_COLORS = [
  'rgb(var(--admin-accent))',
  'rgb(var(--admin-chart-blue))',
  'rgb(var(--admin-chart-green))',
  'rgb(var(--admin-chart-purple))',
  'rgb(var(--admin-chart-orange))',
  'rgb(var(--admin-chart-cyan))',
  'rgb(var(--admin-chart-yellow))',
  'rgb(var(--admin-chart-gray))',
];

function DevicePieTooltipContent({ active, payload }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const label = entry.name ?? entry.payload?.label ?? '';
  const count = entry.value ?? entry.payload?.count ?? 0;
  return (
    <div
      className="rounded-md border border-dark-border px-3 py-2 text-sm text-dark-text shadow-lg"
      style={{ backgroundColor: 'rgb(var(--admin-panel))' }}
    >
      <span className="text-dark-text">
        {label} : {count}
      </span>
    </div>
  );
}

function formatDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - TIMESERIES_DAYS);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatMonthRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - MONTHS_RANGE + 1);
  return {
    startMonth: start.toISOString().slice(0, 7),
    endMonth: end.toISOString().slice(0, 7),
  };
}

/** Fill all dates between startDate and endDate; missing dates get count 0. */
function fillTimeseriesWithZero(series, startDateStr, endDateStr) {
  if (!series || series.length === 0) {
    const out = [];
    const start = new Date(startDateStr + 'T00:00:00Z');
    const end = new Date(endDateStr + 'T00:00:00Z');
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      out.push({ date: d.toISOString().slice(0, 10), count: 0 });
    }
    return out;
  }
  const countByDate = {};
  series.forEach(({ date, count }) => {
    const key = typeof date === 'string' ? date.slice(0, 10) : date;
    countByDate[key] = (countByDate[key] || 0) + (count ?? 0);
  });
  const out = [];
  const start = new Date(startDateStr + 'T00:00:00Z');
  const end = new Date(endDateStr + 'T00:00:00Z');
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const dateStr = d.toISOString().slice(0, 10);
    out.push({ date: dateStr, count: countByDate[dateStr] ?? 0 });
  }
  return out;
}

function localizeDistributionItems(items, unknownLabel) {
  return items.map((it) => ({
    label: it.label === 'unknown' ? unknownLabel : it.label,
    count: it.count,
  }));
}

function DeviceDistributionPieCard({ title, data, isLoading, showPlatformLegendIcons = false }) {
  return (
    <DashboardPanel title={title} className="xl:col-span-4">
      {isLoading ? (
        <ChartSkeleton heightClass={CHART_HEIGHT.panel} />
      ) : data.length === 0 ? (
        <p className={`${CHART_HEIGHT.panel} flex items-center justify-center text-dark-text-secondary`}>
          {t('dashboard.noData')}
        </p>
      ) : (
        <div className={`${CHART_HEIGHT.panel} animate-fade-in`}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={64}
                label={({ label, percent }) =>
                  percent >= 0.05 ? `${label} (${(percent * 100).toFixed(0)}%)` : ''
                }
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={entry.label}
                    fill={DEVICE_PIE_COLORS[index % DEVICE_PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={<DevicePieTooltipContent />}
                contentStyle={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}
                itemStyle={{ display: 'none' }}
                labelStyle={{ display: 'none' }}
                cursor={{ fill: 'rgba(255, 255, 255, 0.08)' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, color: 'rgb(var(--admin-secondary))' }}
                formatter={(value) =>
                  showPlatformLegendIcons ? (
                    <PlatformLegendLabel label={value} />
                  ) : (
                    <span className="text-dark-text-secondary">{value}</span>
                  )
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </DashboardPanel>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { startDate, endDate } = formatDateRange();
  const { startMonth, endMonth } = formatMonthRange();

  const overviewQuery = useQuery({
    queryKey: ['dashboardOverview', apiEnvironment],
    queryFn: () => getDashboardOverview(token),
    enabled: !!token,
  });

  const timeseriesQuery = useQuery({
    queryKey: ['signupsTimeseries', apiEnvironment, startDate, endDate],
    queryFn: () => getSignupsTimeseries(startDate, endDate, token),
    enabled: !!token,
  });

  const byMonthQuery = useQuery({
    queryKey: ['signupsByMonth', apiEnvironment, startMonth, endMonth],
    queryFn: () => getSignupsByMonth(startMonth, endMonth, token),
    enabled: !!token,
  });

  const devicesRegistrationQuery = useQuery({
    queryKey: ['devicesRegistrationSummary', apiEnvironment],
    queryFn: () => getDevicesRegistrationSummary(token),
    enabled: !!token,
  });

  const devicesVersionQuery = useQuery({
    queryKey: ['devicesDistribution', apiEnvironment, 'app_version'],
    queryFn: () => getDevicesDistribution('app_version', token),
    enabled: !!token,
  });

  const devicesPlatformQuery = useQuery({
    queryKey: ['devicesDistribution', apiEnvironment, 'app_platform'],
    queryFn: () => getDevicesDistribution('app_platform', token),
    enabled: !!token,
  });

  useEffect(() => {
    const err =
      overviewQuery.error ||
      timeseriesQuery.error ||
      byMonthQuery.error ||
      devicesRegistrationQuery.error ||
      devicesVersionQuery.error ||
      devicesPlatformQuery.error;
    if (err?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [
    overviewQuery.error,
    timeseriesQuery.error,
    byMonthQuery.error,
    devicesRegistrationQuery.error,
    devicesVersionQuery.error,
    devicesPlatformQuery.error,
    logout,
    navigate,
  ]);

  const overview = overviewQuery.data;
  const timeseriesRaw = timeseriesQuery.data?.series ?? [];
  const timeseriesData = fillTimeseriesWithZero(timeseriesRaw, startDate, endDate);
  const byMonthData = byMonthQuery.data?.series ?? [];

  const isRefreshing =
    overviewQuery.isRefetching ||
    timeseriesQuery.isRefetching ||
    byMonthQuery.isRefetching ||
    devicesRegistrationQuery.isRefetching ||
    devicesVersionQuery.isRefetching ||
    devicesPlatformQuery.isRefetching;
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      overviewQuery.refetch(),
      timeseriesQuery.refetch(),
      byMonthQuery.refetch(),
      devicesRegistrationQuery.refetch(),
      devicesVersionQuery.refetch(),
      devicesPlatformQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: ['dashboardActivityTimeseries'] }),
      queryClient.invalidateQueries({ queryKey: ['gaDashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['gaBreakdown'] }),
    ]);
  }, [
    overviewQuery,
    timeseriesQuery,
    byMonthQuery,
    devicesRegistrationQuery,
    devicesVersionQuery,
    devicesPlatformQuery,
    queryClient,
  ]);

  const handleAuthSectionError = useCallback(
    (err) => {
      if (err?.status === 401) {
        logout();
        navigate('/login', { replace: true });
      }
    },
    [logout, navigate],
  );

  const devicesRegistration = devicesRegistrationQuery.data;
  const unknownLabel = t('dashboard.devices.unknown') || 'unknown';
  const versionData = useMemo(() => {
    const items = devicesVersionQuery.data?.items ?? [];
    return localizeDistributionItems(items, unknownLabel);
  }, [devicesVersionQuery.data, unknownLabel]);
  const platformData = useMemo(() => {
    const items = devicesPlatformQuery.data?.items ?? [];
    return localizeDistributionItems(items, unknownLabel);
  }, [devicesPlatformQuery.data, unknownLabel]);

  const registeredCount = devicesRegistration?.registered_count ?? 0;
  const unregisteredCount = devicesRegistration?.unregistered_count ?? 0;
  const totalUsers = devicesRegistration?.total_users ?? 0;
  const registrationRate =
    totalUsers > 0 ? Math.round((registeredCount / totalUsers) * 100) : 0;

  const activeUsers =
    overview?.users_summary?.active_users ?? overview?.users_summary?.ActiveUsers ?? '-';
  const loggedInUsers =
    overview?.users_summary?.logged_in_users ?? overview?.users_summary?.LoggedInUsers ?? '-';

  return (
    <div className="min-h-screen bg-dark-bg py-6 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-none space-y-4">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-dark-border/60 bg-dark-bg/95 py-3 backdrop-blur-sm">
          <div>
            <h1 className="text-2xl font-bold text-dark-text sm:text-3xl">
              {t('common.brandName')}
            </h1>
            <p className="mt-0.5 text-sm text-dark-text-secondary">{t('dashboard.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex shrink-0 items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-dark-text transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
            title={t('dashboard.refresh') || 'Refresh'}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">{t('dashboard.refresh') || 'Refresh'}</span>
          </button>
        </div>

        <AnalyticsSummary onError={handleAuthSectionError} />

        {/* Signup KPIs */}
        {overview ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            <DashboardKpi
              label={t('dashboard.totalSignups')}
              value={
                overview.total_signup_users ??
                overview.users_summary?.total_users ??
                overview.users_summary?.TotalUsers ??
                '-'
              }
              size="primary"
            />
            <DashboardKpi
              label={t('dashboard.signupsToday')}
              value={overview.signups_today ?? '-'}
              accent
              size="primary"
            />
            <DashboardKpi
              label={t('dashboard.signupsThisWeek')}
              value={overview.signups_this_week ?? '-'}
              accent
              size="primary"
            />
            <DashboardKpi
              label={t('dashboard.activeUsers')}
              value={activeUsers}
              size="primary"
            />
            <DashboardKpi
              label={t('dashboard.loggedInUsers')}
              value={loggedInUsers}
              size="primary"
            />
          </div>
        ) : overviewQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[88px] animate-pulse rounded-lg border border-dark-border bg-dark-card"
              />
            ))}
          </div>
        ) : null}

        {/* Signup charts + devices bento */}
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <DashboardPanel
            title={t('dashboard.signupsByDate')}
            className="xl:col-span-7"
          >
            {timeseriesQuery.isLoading ? (
              <ChartSkeleton heightClass={CHART_HEIGHT.panel} />
            ) : timeseriesData.length === 0 ? (
              <p
                className={`${CHART_HEIGHT.panel} flex items-center justify-center text-dark-text-secondary`}
              >
                {t('dashboard.noData')}
              </p>
            ) : (
              <div className={`${CHART_HEIGHT.panel} animate-fade-in`}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={timeseriesData}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="signupAreaFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_PRIMARY} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={CHART_PRIMARY} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis
                      dataKey="date"
                      stroke={CHART_AXIS_STROKE}
                      fontSize={11}
                      tickFormatter={formatChartDate}
                    />
                    <YAxis stroke={CHART_AXIS_STROKE} fontSize={11} width={32} />
                    <Tooltip {...darkChartTooltipProps} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      name={t('dashboard.signupsCount')}
                      stroke={CHART_PRIMARY}
                      strokeWidth={1.75}
                      fill="url(#signupAreaFill)"
                      dot={false}
                      activeDot={{ r: 3, fill: CHART_PRIMARY }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </DashboardPanel>

          <DashboardPanel
            title={t('dashboard.signupsByMonth')}
            className="xl:col-span-5"
          >
            {byMonthQuery.isLoading ? (
              <ChartSkeleton heightClass={CHART_HEIGHT.panel} />
            ) : byMonthData.length === 0 ? (
              <p
                className={`${CHART_HEIGHT.panel} flex items-center justify-center text-dark-text-secondary`}
              >
                {t('dashboard.noData')}
              </p>
            ) : (
              <div className={`${CHART_HEIGHT.panel} animate-fade-in`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={byMonthData}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis dataKey="month" stroke={CHART_AXIS_STROKE} fontSize={10} />
                    <YAxis stroke={CHART_AXIS_STROKE} fontSize={11} width={32} />
                    <Tooltip {...darkChartTooltipProps} />
                    <Bar
                      dataKey="count"
                      name={t('dashboard.signupsCount')}
                      fill={CHART_PRIMARY}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </DashboardPanel>

          <DashboardPanel
            title={t('dashboard.devices.title')}
            className="xl:col-span-4"
          >
            {devicesRegistrationQuery.isLoading ? (
              <ChartSkeleton heightClass={CHART_HEIGHT.panel} />
            ) : (
              <div className={`flex flex-col justify-between gap-4 ${CHART_HEIGHT.panel}`}>
                <div className="space-y-4">
                  <div>
                    <p className="mb-1 text-xs text-dark-text-secondary">
                      {t('dashboard.devices.registered')}
                    </p>
                    <p className="text-2xl font-semibold text-dark-text">
                      {devicesRegistration?.registered_count ?? '-'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-dark-text-secondary">
                      {t('dashboard.devices.unregistered')}
                    </p>
                    <p className="text-2xl font-semibold text-red-400">
                      {devicesRegistration?.unregistered_count ?? '-'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-dark-text-secondary">
                      {t('dashboard.devices.totalUsers')}
                    </p>
                    <p className="text-2xl font-semibold text-dark-text">
                      {devicesRegistration?.total_users ?? '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <div className="mb-1.5 flex justify-between text-xs text-dark-text-secondary">
                    <span>{t('dashboard.devices.registrationRate')}</span>
                    <span className="font-medium text-dark-text">{registrationRate}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-dark-bg">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{
                        width: `${Math.min(100, registrationRate)}%`,
                      }}
                    />
                  </div>
                  {(registeredCount > 0 || unregisteredCount > 0) && (
                    <div className="mt-2 flex h-1.5 overflow-hidden rounded-full">
                      <div
                        className="bg-primary"
                        style={{
                          width: `${
                            (registeredCount / (registeredCount + unregisteredCount)) * 100
                          }%`,
                        }}
                      />
                      <div
                        className="bg-red-400/80"
                        style={{
                          width: `${
                            (unregisteredCount / (registeredCount + unregisteredCount)) * 100
                          }%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </DashboardPanel>

          <DeviceDistributionPieCard
            title={t('dashboard.devices.versionChart')}
            data={versionData}
            isLoading={devicesVersionQuery.isLoading}
          />
          <DeviceDistributionPieCard
            title={t('dashboard.devices.platformChart')}
            data={platformData}
            isLoading={devicesPlatformQuery.isLoading}
            showPlatformLegendIcons
          />
        </div>

        <OpsPulseSection overview={overview} onError={handleAuthSectionError} />

      </div>
    </div>
  );
}
